import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { chartOfAccountsApi } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Plus, Folder, FileText, ChevronRight, Info, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const CODE_GUIDE = [
  { range: '1000–1599', type: 'Assets', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', desc: 'Cash, Loans, Investments, Leases, PP&E' },
  { range: '2000–2499', type: 'Liabilities', color: 'bg-red-50 text-red-700 border-red-200', desc: 'Customer Deposits, Borrowings, Bonds' },
  { range: '3000–3299', type: 'Equity', color: 'bg-violet-50 text-violet-700 border-violet-200', desc: 'Share Capital, Retained Earnings, Reserves' },
  { range: '4000–4299', type: 'Income', color: 'bg-blue-50 text-blue-700 border-blue-200', desc: 'Interest Income, Fee Income, Investment Income' },
  { range: '5000–5199', type: 'Financial Expenses', color: 'bg-amber-50 text-amber-700 border-amber-200', desc: 'Interest Expense, Provisions, Write-offs' },
  { range: '6000–6999', type: 'Operating Expenses', color: 'bg-orange-50 text-orange-700 border-orange-200', desc: 'Salaries, Rent, IT, Marketing, Depreciation' },
];

const TYPE_ORDER = ['asset', 'liability', 'equity', 'income', 'expense'];
const TYPE_COLORS = {
  asset: 'text-emerald-700 bg-emerald-50',
  liability: 'text-red-700 bg-red-50',
  equity: 'text-violet-700 bg-violet-50',
  income: 'text-blue-700 bg-blue-50',
  expense: 'text-amber-700 bg-amber-50',
};

const ChartOfAccountsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTypes, setExpandedTypes] = useState({});
  const [showGuide, setShowGuide] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ account_code: '', account_name: '', account_type: 'asset', description: '' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { const res = await chartOfAccountsApi.list(); setAccounts(res.data); }
    catch (e) { console.error(e); }
    setLoading(false);
  };

  const filtered = search
    ? accounts.filter(a => (a.account_code || '').includes(search) || (a.account_name || '').toLowerCase().includes(search.toLowerCase()))
    : accounts;

  const grouped = filtered.reduce((acc, a) => {
    const type = a.account_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(a);
    return acc;
  }, {});

  const sortedTypes = TYPE_ORDER.filter(t => grouped[t]);

  const toggle = (type) => setExpandedTypes(prev => ({ ...prev, [type]: !prev[type] }));

  const handleCreate = async () => {
    if (!form.account_code || !form.account_name) { toast.error('Code and name required'); return; }
    try {
      await chartOfAccountsApi.create(form);
      toast.success(`Account ${form.account_code} created`);
      setShowCreate(false);
      setForm({ account_code: '', account_name: '', account_type: 'asset', description: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const totalByType = (type) => {
    const accs = grouped[type] || [];
    return accs.reduce((sum, a) => sum + (a.balance || 0), 0);
  };

  return (
    <AdminSubPage title="Chart of Accounts" breadcrumb={['Accounting', 'Chart of Accounts']} backPath="/dashboard/accounting">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search by code or name..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 text-sm" data-testid="coa-search" />
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowGuide(!showGuide)} className="text-xs gap-1.5" data-testid="coa-guide-btn">
            <Info className="w-3.5 h-3.5" /> Code Guide
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} className="bg-[#1f1f1f] text-white text-xs gap-1.5" data-testid="coa-create-btn">
            <Plus className="w-3.5 h-3.5" /> Add Account
          </Button>
        </div>

        {showGuide && (
          <div className="bg-white border border-gray-200 p-4" data-testid="coa-guide-panel">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">GL Code Numbering Convention</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {CODE_GUIDE.map(g => (
                <div key={g.range} className={`px-3 py-2 border rounded text-xs ${g.color}`}>
                  <span className="font-mono font-bold">{g.range}</span>
                  <span className="mx-1.5">|</span>
                  <span className="font-semibold">{g.type}</span>
                  <p className="text-[10px] opacity-75 mt-0.5">{g.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-2">Parent accounts (e.g., 1000, 2000) act as category headers. Sub-accounts (e.g., 2001, 6010) hold specific balances. You can add custom accounts to any range.</p>
          </div>
        )}

        <div className="bg-white border border-gray-200">
          {loading ? <div className="text-center py-8 text-gray-400">Loading...</div> :
          sortedTypes.length === 0 ? <div className="text-center py-8 text-gray-400">No accounts found</div> :
          sortedTypes.map(type => (
            <div key={type} className="border-b last:border-0">
              <button onClick={() => toggle(type)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left" data-testid={`coa-type-${type}`}>
                <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${expandedTypes[type] ? 'rotate-90' : ''}`} />
                <Folder className="w-4 h-4 text-[#C5A059]" />
                <span className={`font-medium text-sm capitalize px-2 py-0.5 rounded ${TYPE_COLORS[type] || ''}`}>{type}</span>
                <span className="text-xs text-gray-400">{(grouped[type] || []).length} accounts</span>
                <span className="text-xs font-mono text-gray-500 ml-auto">{formatCurrency(Math.abs(totalByType(type)))}</span>
              </button>
              {expandedTypes[type] && (
                <div className="bg-gray-50/50">
                  <div className="grid grid-cols-[80px_1fr_100px] px-4 py-1.5 pl-12 text-[10px] font-semibold uppercase tracking-wider text-gray-400 border-t border-gray-100">
                    <span>Code</span><span>Account Name</span><span className="text-right">Balance</span>
                  </div>
                  {(grouped[type] || []).sort((a, b) => (a.account_code || '').localeCompare(b.account_code || '')).map(a => (
                    <div key={a.id} className="grid grid-cols-[80px_1fr_100px] items-center gap-3 px-4 py-2 pl-12 border-t border-gray-100 hover:bg-white" data-testid={`coa-account-${a.account_code}`}>
                      <span className="text-sm text-gray-700 font-mono font-medium">{a.account_code}</span>
                      <div className="flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{a.account_name}</span>
                        {a.is_contra && <span className="text-[9px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">CONTRA</span>}
                      </div>
                      <span className={`text-xs font-mono text-right ${(a.balance || 0) < 0 ? 'text-red-600' : 'text-gray-600'}`}>{formatCurrency(Math.abs(a.balance || 0))}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md" aria-describedby="coa-create-desc">
          <DialogHeader><DialogTitle>Add GL Account</DialogTitle></DialogHeader>
          <p id="coa-create-desc" className="sr-only">Create a new chart of accounts entry</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Account Code *</Label><Input value={form.account_code} onChange={e => setForm({...form, account_code: e.target.value})} placeholder="e.g. 6150" className="font-mono" data-testid="coa-new-code" /></div>
              <div><Label>Account Type *</Label>
                <Select value={form.account_type} onValueChange={v => setForm({...form, account_type: v})}>
                  <SelectTrigger data-testid="coa-new-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asset">Asset</SelectItem>
                    <SelectItem value="liability">Liability</SelectItem>
                    <SelectItem value="equity">Equity</SelectItem>
                    <SelectItem value="income">Income / Revenue</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Account Name *</Label><Input value={form.account_name} onChange={e => setForm({...form, account_name: e.target.value})} placeholder="e.g. Cleaning Services" data-testid="coa-new-name" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Optional description" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#1f1f1f] text-white" data-testid="coa-create-submit">Create Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default ChartOfAccountsPage;
